import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTopUpAmount } from '@/hooks/billing/use-top-up-amount.hook'
import { cn } from '@/lib/utils'

import { BillingCustomAmountInput } from './billing-custom-amount-input.atom'

export interface BillingTopUpModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onTopUp: (amount: number) => void
  isLoading?: boolean
  className?: string
}

const PRESET_AMOUNT_LIST = [10, 50, 100, 200]

const TopUpModalHeader: React.FC = () => (
  <DialogHeader>
    <DialogTitle>Add Credits</DialogTitle>
    <DialogDescription>
      Select an amount or enter a custom value to add credits to your account.
    </DialogDescription>
  </DialogHeader>
)

interface TopUpModalFooterProps {
  currentAmount: number | null
  isLoading: boolean
  isValidAmount: boolean
  onCancel: () => void
  onTopUp: () => void
}

const TopUpModalFooter: React.FC<TopUpModalFooterProps> = ({
  currentAmount,
  isLoading,
  isValidAmount,
  onCancel,
  onTopUp,
}) => (
  <DialogFooter>
    <Button variant="outline" onClick={onCancel} disabled={isLoading}>
      Cancel
    </Button>
    <TopUpSubmitButton
      amount={currentAmount}
      isLoading={isLoading}
      isValid={isValidAmount}
      onClick={onTopUp}
    />
  </DialogFooter>
)

const BillingTopUpModalBase: React.FC<BillingTopUpModalProps> = ({
  isOpen,
  onOpenChange,
  onTopUp,
  isLoading = false,
  className,
}): ReactNode => {
  const {
    selectedAmount,
    customAmount,
    handlePresetClick,
    handleCustomAmountChange,
    getCurrentAmount,
    isValidAmount,
    validationError,
  } = useTopUpAmount(isOpen)
  const currentAmount = getCurrentAmount()
  const handleTopUp = (): void => {
    if (currentAmount && currentAmount > 0) onTopUp(currentAmount)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('billing-top-up-modal sm:max-w-md', className)}>
        <TopUpModalHeader />
        <div className="space-y-4 py-4">
          <PresetAmountGrid selectedAmount={selectedAmount} onSelect={handlePresetClick} />
          <BillingCustomAmountInput
            id="custom-amount"
            value={customAmount}
            onChange={handleCustomAmountChange}
            error={validationError}
            inputClassName="billing-top-up-custom-input"
          />
        </div>
        <TopUpModalFooter
          currentAmount={currentAmount}
          isLoading={isLoading}
          isValidAmount={isValidAmount}
          onCancel={() => onOpenChange(false)}
          onTopUp={handleTopUp}
        />
      </DialogContent>
    </Dialog>
  )
}

const PresetAmountGrid: React.FC<{
  selectedAmount: number | null
  onSelect: (amount: number) => void
}> = ({ selectedAmount, onSelect }) => (
  <div className="grid grid-cols-4 gap-2">
    {PRESET_AMOUNT_LIST.map((amount) => (
      <Button
        key={amount}
        variant={selectedAmount === amount ? 'default' : 'outline'}
        className="billing-top-up-preset"
        onClick={() => onSelect(amount)}
      >
        ${amount}
      </Button>
    ))}
  </div>
)

interface TopUpSubmitButtonProps {
  amount: number | null
  isLoading: boolean
  isValid: boolean
  onClick: () => void
}

const TopUpSubmitButton: React.FC<TopUpSubmitButtonProps> = ({
  amount,
  isLoading,
  isValid,
  onClick,
}) => (
  <Button className="billing-top-up-button" onClick={onClick} disabled={!isValid || isLoading}>
    {isLoading ? (
      <>
        <Loader2Icon className="mr-2 size-4 animate-spin" />
        Processing...
      </>
    ) : (
      `Add $${amount || 0}`
    )}
  </Button>
)

export const BillingTopUpModal = memo(BillingTopUpModalBase)

BillingTopUpModal.displayName = 'BillingTopUpModal'
